// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A timeline item component for proposed event groups.
 * This module provides a detailed view of a single proposed
 * event group, showing origin and destination locations
 * with links to location details. It includes a review
 * dialog for managing the event group.
 *
 * Key features:
 * - Location display
 * - Navigation links
 * - Review dialog
 * - Empty state handling
 * - Grid layout
 */
import { Asset, EventGroup } from '@/asset/api/types';
import { EventGroupDialog } from '@/asset/components/event-group-dialog';
import { Link } from '@/components/link';
import { WarningAmberRounded } from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { DateTime } from 'luxon';
import { Fragment } from 'react';

/**
 * Props for configuring the AssetProposedEventGroupTimelineItem component.
 *
 * @interface Props
 */
interface Props {
  /**
   * The asset associated with the event group.
   * Used for context in the review dialog.
   */
  asset: Asset;

  /**
   * The event group to display.
   * Contains origin, destination, and configuration details.
   */
  eventGroup: EventGroup;
}

/**
 * A timeline item component for a proposed event group.
 * Displays origin and destination locations with navigation
 * links and provides a review dialog for managing the event
 * group. Handles empty states for missing locations.
 *
 * Features:
 * - Location display
 * - Navigation links
 * - Review dialog
 * - Empty state handling
 * - Grid layout
 * - Dialog integration
 *
 * Usage example:
 * ```tsx
 * function EventGroupList({ asset, eventGroups }: Props) {
 *   return eventGroups.map(eventGroup => (
 *     <AssetProposedEventGroupTimelineItem
 *       key={eventGroup.id}
 *       asset={asset}
 *       eventGroup={eventGroup}
 *     />
 *   ));
 * }
 * ```
 */
export function AssetProposedEventGroupTimelineItem(props: Props) {
  // Dialog state management
  const dialog = useDisclosure();

  // Extract the ship event from props.eventGroup.events
  const shipEvent = props.eventGroup.events.find(
    (event) => event.type.name === 'Ship'
  );
  // Extract the deliver event from props.eventGroup.events
  const deliverEvent = props.eventGroup.events.find(
    (event) => event.type.name === 'Deliver'
  );

  return (
    <Fragment>
      <EventGroupDialog
        isOpen={dialog.isOpen}
        onClose={dialog.close}
        asset={props.asset}
        eventGroup={props.eventGroup}
        configuration={props.eventGroup.configuration}
      />
      <Box display="grid" gridTemplateColumns="subgrid" gridColumn="span 2">
        <Typography variant="subtitle2">Origin</Typography>
        {props.eventGroup.origin === null ? (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        ) : (
          <Link
            to="/dashboard/location"
            variant="body2"
            color="text.primary"
            search={{ location: props.eventGroup.origin.key }}
          >
            {props.eventGroup.origin.name}
          </Link>
        )}
        {shipEvent && (
          <Typography variant="subtitle2">Promise ship date</Typography>
        )}
        {shipEvent &&
          (!shipEvent.promiseDate ? (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color={'text.primary'}>
                {shipEvent.promiseDate.toLocaleString({
                  dateStyle: 'medium',
                })}
              </Typography>
              {shipEvent.promiseDate < DateTime.now().startOf('day') &&
                shipEvent.occurredAt === null && (
                  <Tooltip arrow title="The promised date has passed">
                    <WarningAmberRounded
                      sx={{ fontSize: 16, color: 'warning.main' }}
                    />
                  </Tooltip>
                )}
            </Stack>
          ))}
        {deliverEvent && (
          <Typography variant="subtitle2">Promise deliver date</Typography>
        )}
        {deliverEvent &&
          (!deliverEvent.promiseDate ? (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          ) : (
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="body2" color="text.primary">
                {deliverEvent.promiseDate.toLocaleString({
                  dateStyle: 'medium',
                })}
              </Typography>
              {deliverEvent.promiseDate < DateTime.now().startOf('day') &&
                deliverEvent.occurredAt === null && (
                  <Tooltip arrow title="The promised date has passed">
                    <WarningAmberRounded
                      sx={{ fontSize: 16, color: 'warning.main' }}
                    />
                  </Tooltip>
                )}
            </Stack>
          ))}
        <Typography variant="subtitle2">Destination</Typography>
        {props.eventGroup.destination === null ? (
          <Typography variant="body2" color="text.secondary">
            None
          </Typography>
        ) : (
          <Link
            to="/dashboard/location"
            variant="body2"
            color="text.primary"
            search={{ location: props.eventGroup.destination.key }}
          >
            {props.eventGroup.destination.name}
          </Link>
        )}
        {shipEvent && (
          <Typography variant="subtitle2">Expected ship date</Typography>
        )}
        {shipEvent &&
          (!shipEvent.expectedAt ? (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          ) : (
            <Typography variant="body2" color={'text.primary'}>
              {shipEvent.expectedAt.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
          ))}
        {deliverEvent && (
          <Typography variant="subtitle2">Expected deliver date</Typography>
        )}
        {deliverEvent &&
          (!deliverEvent.expectedAt ? (
            <Typography variant="body2" color="text.secondary">
              Not set
            </Typography>
          ) : (
            <Typography variant="body2" color="text.primary">
              {deliverEvent.expectedAt.toLocaleString({
                dateStyle: 'medium',
              })}
            </Typography>
          ))}
      </Box>
      <Button
        variant="contained"
        color="secondary"
        onClick={dialog.open}
        sx={{ alignSelf: 'center' }}
      >
        Review
      </Button>
    </Fragment>
  );
}
